import { useSelector } from "react-redux";

/**
 * Config for a single field of a page, as delivered by the login API inside
 * each claim's `pageFieldShowHideConfigs`.
 */
export interface PageFieldConfig {
  /** Whether the field should be rendered at all. Defaults to true when the
   * backend sends no config for the field. */
  isShow: boolean;
  /** Backend-driven control type (e.g. "textbox", "phone-international").
   * Null when no config is present for the field. */
  controlType: string | null;
}

/**
 * Reads the show/hide + control-type config for a given page field out of the
 * Redux `Menus` (the login API claims). Menus is an array of modules, each with
 * a `claims` array of pages; the page whose `id` matches `pageId` carries a
 * `pageFieldShowHideConfigs` array (or its legacy `pageFields` property) keyed
 * by field name.
 *
 * Returns `null` when no config exists for the field, so callers can tell
 * "backend sent no config" apart from "config present". This keeps existing
 * tenants (no config) on their default rendering.
 */
const usePageFieldConfig = (
  pageId: number,
  fieldName: string
): PageFieldConfig | null => {
  const menus = useSelector((state: any) => state?.Reducer?.Menus);

  if (!Array.isArray(menus)) return null;

  for (const module of menus) {
    const claim = module?.claims?.find((c: any) => c?.id === pageId);
    const fieldConfig = claim?.pageFieldShowHideConfigs;
    const pageFields = Array.isArray(fieldConfig)
      ? fieldConfig
      : fieldConfig?.pageFields;
    if (Array.isArray(pageFields)) {
      const field = pageFields.find((f: any) => f?.field === fieldName);
      if (field) {
        return {
          isShow: field.isShow ?? true,
          controlType: field.controlType ?? null,
        };
      }
    }
  }

  return null;
};

export default usePageFieldConfig;
